const STORAGE_KEY = 'command-center:read-only-impersonation';
const HASH_PARAMETER = 'impersonation';

export interface BrowserImpersonationContext {
  sessionId: string;
  targetAppUserId: string;
  targetName: string;
  targetEmail: string;
  organizationId: string;
  organizationName: string;
  appId: string;
  portalLabel: string;
  role: 'client_admin' | 'client_member';
  expiresAt: string;
}

export interface BrowserImpersonationState {
  sessionToken: string;
  impersonation: BrowserImpersonationContext;
}

export async function initializeBrowserImpersonation(
  apiBaseUrl: string,
): Promise<BrowserImpersonationState | null> {
  const exchangeToken = readExchangeToken();
  if (!exchangeToken) return readBrowserImpersonation();
  removeExchangeTokenFromLocation();
  const response = await fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/impersonation/exchange`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ exchangeToken }),
  });
  const body = await response.json().catch(() => null) as {
    sessionToken?: string;
    impersonation?: BrowserImpersonationContext;
    error?: { message?: string };
  } | null;
  if (!response.ok || !body?.sessionToken || !body.impersonation) {
    throw new Error(
      body?.error?.message ?? 'The View as User session could not be opened.',
    );
  }
  const state = {
    sessionToken: body.sessionToken,
    impersonation: body.impersonation,
  };
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function readBrowserImpersonation(): BrowserImpersonationState | null {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<BrowserImpersonationState>;
    if (
      typeof parsed.sessionToken !== 'string'
      || !parsed.impersonation
      || typeof parsed.impersonation.sessionId !== 'string'
    ) {
      return null;
    }
    return parsed as BrowserImpersonationState;
  } catch {
    return null;
  }
}

export function clearBrowserImpersonation(): void {
  window.sessionStorage.removeItem(STORAGE_KEY);
}

export async function impersonationAwareFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response | undefined> {
  const state = readBrowserImpersonation();
  if (!state) return undefined;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Impersonation ${state.sessionToken}`);
  return fetch(input, { ...init, headers });
}

export async function exitBrowserImpersonation(
  apiBaseUrl: string,
  returnUrl: string,
): Promise<void> {
  const state = readBrowserImpersonation();
  if (state) {
    await fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/impersonation/exit`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Impersonation ${state.sessionToken}`,
      },
    }).catch(() => undefined);
  }
  clearBrowserImpersonation();
  window.location.assign(returnUrl);
}

function readExchangeToken(): string | undefined {
  const fragment = window.location.hash.replace(/^#/, '');
  if (!fragment) return undefined;
  const token = new URLSearchParams(fragment).get(HASH_PARAMETER)?.trim();
  return token || undefined;
}

function removeExchangeTokenFromLocation(): void {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
  fragment.delete(HASH_PARAMETER);
  url.hash = fragment.toString() ? `#${fragment.toString()}` : '';
  window.history.replaceState(window.history.state, '', url);
}
